import {
  takeLatest,
  takeLeading,
  takeEvery,
  call,
  put,
  select,
  delay,
} from 'redux-saga/effects';
import { navigate } from '../navigation';

import {
  config,
  storeAccessToken,
  deleteAccessToken,
  storeRefreshToken,
  getFlagsFromAccessToken,
  getIsLeadFromAccessToken,
  setFlagsAction,
  exchangeAuthCodeForToken,
  exchangeUsernameForToken,
  exchangeIdTokenForToken,
  exchangeRefreshTokenForToken,
  exchangeOidcAuthCodeForToken,
  exchangeOidcIdTokenForToken,
  exchangeOidcLoginAssertionForToken,
  refreshOidcSession,
  revokeOidcSession,
  isOidcSession,
  storeOidcSession,
  TOKEN_PROVIDERS,
  actions as coreActions,
  apiImpersonate,
  registerUser,
} from '@formue-app/core';

import { actions } from './actions';
import {
  initAnalytics,
  resetAnalytics,
} from '../../services/tracking/analytics';
import { actions as appActions } from '../app/actions';
import { actions as errorsActions } from '../errors/actions';

const {
  ui: { user: uiUserActions, impersonation: uiImpersonationActions },
  entities: { users: usersActions },
} = coreActions;

function* startRegistration() {
  yield takeLeading(actions.startRegistration, function* ({ payload }) {
    const { data, codeVerifier } = payload;

    try {
      const result = yield call(registerUser, data, codeVerifier);

      if (result) {
        yield put(
          actions.finishRegistration({
            authCode: result.authCode,
            codeVerifier,
          })
        );
      } else {
        throw new Error('No result from registration');
      }
    } catch (error) {
      yield put(actions.failedRegistration(error));
    }
  });
}

function* failedRegistration() {
  yield takeLeading(actions.failedRegistration, function* ({ payload }) {
    console.log(payload);
  });
}
function* finishRegistration() {
  yield takeLeading(actions.finishRegistration, function* ({ payload }) {
    const { authCode, codeVerifier } = payload;

    yield put(
      actions.startAuthentication({
        exchange: 'code',
        credentials: {
          code: authCode,
          codeVerifier,
        },
      })
    );
  });
}

/**
 * The `/oidc` counterpart of the exchanges in `startAuthentication`. This one
 * is wrapped as a whole: a failed exchange lands the user on the login page
 * rather than kill the watcher. In the legacy branch only the code exchange is
 * wrapped; its other exchanges still let a failure end the watcher.
 */
function* startOidcAuthentication({ exchange, credentials, pathname }) {
  try {
    let accessToken;
    let refreshToken;

    switch (exchange) {
      case 'code': {
        const { code, codeVerifier } = credentials;
        const { authCallbackUrl: redirectUrl } = config.app;
        ({ accessToken, refreshToken } = yield call(
          exchangeOidcAuthCodeForToken,
          code,
          codeVerifier,
          redirectUrl
        ));
        break;
      }
      case 'idToken': {
        ({ accessToken, refreshToken } = yield call(
          exchangeOidcIdTokenForToken,
          credentials
        ));
        break;
      }
      case 'loginAssertion': {
        ({ accessToken, refreshToken } = yield call(
          exchangeOidcLoginAssertionForToken,
          credentials.assertion
        ));
        break;
      }
      case 'refreshToken': {
        // The refresh reads the stored refresh token itself and persists the
        // rotated pair before it resolves, so `credentials` is not used here.
        ({ accessToken, refreshToken } = yield call(refreshOidcSession));
        break;
      }
      default: {
        throw new Error('No authentication exchange specified');
      }
    }

    if (exchange !== 'refreshToken') {
      // Persist before the token reaches redux, so a reload mid-bootstrap finds
      // a complete session rather than an access token without its provider.
      yield call(storeOidcSession, { accessToken, refreshToken });
    }

    yield put(actions.setAccessToken(accessToken));
    if (refreshToken) {
      yield put(actions.setRefreshToken(refreshToken));
    }
    yield put(actions.finishAuthentication(pathname));
  } catch (error) {
    yield put(actions.failedAuthentication(error));

    if (exchange === 'refreshToken') {
      // A failed refresh is a dead session: go through the regular 401 route so
      // SSO users bounce back to /auth/sso for a fresh id token.
      yield put(errorsActions.handleAuthError());
    } else {
      yield call(navigate, '/login', { state: { error: 'AccessDenied' } });
    }
  }
}

function* startAuthentication() {
  yield takeLeading(actions.startAuthentication, function* ({ payload }) {
    const { exchange, credentials, pathname, provider } = payload;

    // A session started on the OIDC provider is exchanged, stored and refreshed
    // against `/oidc`. The legacy branch below is untouched.
    if (provider === TOKEN_PROVIDERS.OIDC) {
      yield call(startOidcAuthentication, { exchange, credentials, pathname });
      return;
    }

    let accessToken;
    let refreshToken;
    switch (exchange) {
      case 'username': {
        try {
          ({ accessToken } = yield call(exchangeUsernameForToken, credentials));
        } catch (error) {
          // A wrong username or password must leave the form usable for another try.
          yield put(actions.failedAuthentication(error));
          return;
        }
        break;
      }
      case 'code': {
        const { code, codeVerifier } = credentials;
        const { authCallbackUrl: redirectUrl } = config.app;
        // In case of bankid we need to redirect the user to the authorization endpoint
        try {
          ({ accessToken, refreshToken } = yield call(
            exchangeAuthCodeForToken,
            code,
            codeVerifier,
            redirectUrl
          ));
        } catch (error) {
          // A spent or mismatched code otherwise leaves the user on the callback
          // spinner for good. getToken has already reported it to Sentry.
          yield put(actions.failedAuthentication(error));
          yield call(navigate, '/login', { state: { error: 'ExchangeFailed' } });
          return;
        }
        break;
      }
      case 'idToken': {
        ({ accessToken } = yield call(exchangeIdTokenForToken, credentials));
        break;
      }
      case 'refreshToken': {
        ({ accessToken, refreshToken } = yield call(
          exchangeRefreshTokenForToken,
          credentials
        ));
        break;
      }
      default: {
        throw new Error('No authentication exchange specified');
      }
    }

    yield put(actions.setAccessToken(accessToken));
    if (refreshToken) {
      yield put(actions.setRefreshToken(refreshToken));
    }
    yield put(actions.finishAuthentication(pathname));
  });
}

function* finishAuthentication() {
  yield takeLeading(actions.finishAuthentication, function* ({ payload }) {
    const pathname = payload;
    const isEmployee = yield select((state) => state.auth.isEmployee);
    if (pathname) {
      yield call(navigate, pathname);
    } else if (isEmployee) {
      yield call(navigate, '/advisor/clients');
    } else {
      yield call(navigate, '/');
    }
    yield put(appActions.init());
  });
}

/**
 * Saves the access token to local storage
 */
function* saveAccessToken() {
  yield takeLatest(actions.setAccessToken, function* ({ payload }) {
    yield storeAccessToken(payload);

    let flags = yield call(getFlagsFromAccessToken, payload);
    yield put(setFlagsAction({ features: flags }));

    // Check access token, and set isLead on user and in flags
    const isLead = yield call(getIsLeadFromAccessToken, payload);
    yield put(setFlagsAction({ isLead: isLead }));
  });
}

/**
 * Deletes the access token from local storage
 */
function* removeAccessToken() {
  yield takeLatest(actions.removeAccessToken, function* () {
    if (yield call(isOidcSession)) {
      // Discarding the access token of an /oidc session means the user has to
      // authenticate again: the refresh token must not be able to renew it, so
      // the whole session goes — from the browser first, then revoked on the
      // server (best effort).
      try {
        yield call(revokeOidcSession);
      } catch {
        // storage is already cleared; a failed revocation changes nothing
      }
    } else {
      yield deleteAccessToken();
    }
  });
}

/**
 * Saves the refresh token to local storage
 */
function* saveRefreshToken() {
  yield takeLatest(actions.setRefreshToken, function* ({ payload }) {
    yield storeRefreshToken(payload);
  });
}

function* logout() {
  yield takeLatest(actions.logout, function* ({ payload }) {
    const { url = '/login', state } = payload;
    yield call(resetAnalytics);

    if (yield call(isOidcSession)) {
      try {
        yield call(revokeOidcSession);
      } catch {
        // Revocation is best effort; the local logout continues regardless.
      }
    }

    yield put(actions.removeAccessToken());
    // Also reset impersonating when logging out as an advisor
    yield put(actions.resetImpersonation());

    // Allow logout action to redirect to any arbitrary url
    if (url.startsWith('https://')) {
      window.location.assign(url);
    } else {
      // `state` carries e.g. { error: 'SessionTimeout' } through to LoginPage.
      yield call(navigate, url, { state });
      // Adding a delay here so that we can be sure that logic that should
      // happen when we remove the access token get's finished before we
      // flush data. In practice this means that we enough time to navigate
      // away from any page that uses the useResource hook before we flush.
      // Without this, we would try to refetch the missing data which would
      // lead to an automatic re-authentication if you used advisor SSO login
      yield delay(500);
      yield put({ type: 'APP/FLUSH' });
    }
  });
}

/**
 * Loads the user that is currently authenticated from the users API
 */
function* loadCurrentUser() {
  yield takeLeading(actions.loadCurrentUser, function* () {
    const impersonationId = yield select((state) => state.auth.impersonationId);
    const userId = yield select((state) => state.auth.userId);
    // Signed out, e.g. logout reaching here through resetImpersonation after the
    // access token is gone: there is no user, and `/v2/users` without an id 404s.
    if (!userId) {
      return;
    }
    if (impersonationId) {
      yield put(usersActions.showRequestBegin(impersonationId));
    }
    yield put(
      usersActions.showRequestBegin({ id: userId, impersonate: false })
    );
  });
}

function* impersonate() {
  yield takeEvery(actions.impersonateUser, function* ({ payload }) {
    const ssid = payload;
    // Store impersonationId in session storage so that we can use it incase
    // the advisors refreshes the app, that way we can bootstrap it with the
    // correct user
    window.sessionStorage.setItem('impersonationId', ssid);

    // Update UI
    yield put(uiUserActions.setIsImpersonating(true));
    yield put(uiUserActions.setImpersonationId(ssid));
    yield put(uiImpersonationActions.saveRecentlyImpersonatedClientId(ssid));
    const token = yield select((state) => state.auth.accessToken);

    // If impersonationId is equal to employeeClientId update UI to reflect that. E.g allow
    // for document download even if one is impersonating
    const employeeClientUserId = yield select(
      (state) => state.auth.employeeClientUser?.id
    );
    if (ssid === employeeClientUserId) {
      yield put(uiUserActions.setIsEmployeeClientUser(true));

      // Load the flags from employeeClientUser to give the same permissions as the employee
      // client user when logged in as "one self".
      let flags = yield call(getFlagsFromAccessToken, token, true);
      yield put(setFlagsAction({ features: flags }));
    } else {
      // Make sure we reset the flags to the authenticated user if we are not impersonating
      // our own employee client user
      let flags = yield call(getFlagsFromAccessToken, token, false);
      yield put(setFlagsAction({ features: flags }));
    }

    // Configure the api client to impersonate
    apiImpersonate(ssid);
    // After the header is set: on a reload while impersonating, the landing
    // view must be attributed to the impersonated client.
    yield call(initAnalytics);

    // Reload the app
    yield put({ type: 'APP/LOAD', payload: ssid });
  });
}

function* resetImpersonation() {
  yield takeEvery(actions.resetImpersonation, function* () {
    window.sessionStorage.removeItem('impersonationId');
    yield put(uiUserActions.setIsImpersonating(false));
    yield put(uiUserActions.setImpersonationId(null));

    apiImpersonate(undefined);

    // Flush all content so that we are sure we don't show another users data after
    // we have changed who we impersonate. We don't use action creator functions here
    // since the APP action creator does not live in the "@formue-app/core" package.
    // this allows us to dispatch events that the different apps that utilize
    // "@formue-app/core" to react to this event differently (or not at all).
    yield put({ type: 'APP/FLUSH' });

    // Load currentuser as we need it to start a new impersonation from the clients page
    yield put(actions.loadCurrentUser());
  });
}

export const sagas = [
  startRegistration,
  finishRegistration,
  failedRegistration,
  startAuthentication,
  finishAuthentication,
  saveAccessToken,
  removeAccessToken,
  saveRefreshToken,
  loadCurrentUser,
  impersonate,
  resetImpersonation,
  logout,
];
